import { Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ActivityLogLisTable } from "./table";
import { ACTIVITY_LOG_COLUMNS } from "./table-config";
import ActivityLogFilters from "./Filters";
import "./style.css";
import {
  fetchActivityLogData,
  markActivityLogUnvisited,
  markActivityLogVisited,
} from "../../apis/invitations.api";
import { useDispatch } from "react-redux";
import {
  DEFAULT_SELECTED_PAGE,
} from "../../constants";
import moment from "moment";

const DEFAULT_SELECTED_PAGE_SIZE = 100;

export default function ActivityLog() {
  const dispatch = useDispatch();
  const [activityLogData, setActivityLogData] = useState<any>({
    data: [],
    total: 0,
    loading: true,
  });
  const [filters, setFilters] = useState({
    selectedPageSize: DEFAULT_SELECTED_PAGE_SIZE,
    selectedPage: DEFAULT_SELECTED_PAGE,
    ambassadorId: "",
    clientId: "",
    status: "",
    from: "",
    to: "",
    visitedFilter: "all",
  });

  const getActivityLogParams = (filterState = filters) => ({
    ambassadorId: filterState.ambassadorId,
    clientId: filterState.clientId,
    status: filterState.status,
    from: filterState.from,
    to: filterState.to,
    page: filterState.selectedPage,
    limit: filterState.selectedPageSize,
    visitedFilter: filterState.visitedFilter,
  });

  useEffect(() => {
    onLoad();
  }, []);

  const onLoad = async () => {
    const response: any = await dispatch(
      fetchActivityLogData({
        ...getActivityLogParams(),
        limit: DEFAULT_SELECTED_PAGE_SIZE,
        page: 1,
      })
    );
    if (response.success) {
      setActivityLogData({ ...response, loading: false });
    } else {
      setActivityLogData({
        data: [],
        total: 0,
        loading: false,
      });
    }
  };

  const handlePageFilters = async ({ selectedPage, selectedPageSize }) => {
    const nextFilters = {
      ...filters,
      selectedPage,
      selectedPageSize,
    };

    setFilters(nextFilters);

    setActivityLogData({
      ...activityLogData,
      loading: true,
    });

    const response: any = await dispatch(
      fetchActivityLogData(getActivityLogParams(nextFilters))
    );
    if (response.success) {
      setActivityLogData({ ...response, loading: false });

      document.getElementById("activity-log-table-wrapper")?.scrollIntoView({
        behavior: "smooth",
      });
    } else {
      setActivityLogData({
        ...activityLogData,
        loading: false,
      });
    }
  };

  const handleFiltersChange = async (searchFilters) => {
    const payload = {
      ...filters,
      selectedPage: 1,
      selectedPageSize: DEFAULT_SELECTED_PAGE_SIZE,
      ambassadorId: searchFilters?.ambassador?.id || "",
      clientId: searchFilters?.vendor?.id || "",
      status: searchFilters?.status?.value || "",
      from:
        searchFilters?.dateRange?.startDate && searchFilters?.dateRange?.endDate
          ? moment(searchFilters.dateRange.startDate).format("YYYY-MM-DD")
          : "",
      to:
        searchFilters?.dateRange?.startDate && searchFilters?.dateRange?.endDate
          ? moment(searchFilters.dateRange.endDate).format("YYYY-MM-DD")
          : "",
    };

    setFilters(payload);

    const response: any = await dispatch(
      fetchActivityLogData(getActivityLogParams(payload))
    );
    if (response.success) {
      setActivityLogData({ ...response, loading: false });
    } else {
      setActivityLogData({
        data: [],
        total: 0,
        loading: false,
      });
    }

    document.getElementById("activity-log-table-wrapper")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const handleVisitedChange = async (
    id: string,
    visited: boolean
  ): Promise<boolean> => {
    if (!id) return false;

    const response: any = await dispatch(
      visited
        ? markActivityLogVisited([id])
        : markActivityLogUnvisited([id])
    );

    if (!response?.success) {
      return false;
    }

    const shouldRemoveRow =
      (filters.visitedFilter === "visited" && !visited) ||
      (filters.visitedFilter === "unvisited" && visited);

    if (shouldRemoveRow) {
      setActivityLogData((prev) => ({
        ...prev,
        data: prev.data?.filter((row) => row._id !== id),
        total: Math.max(0, (prev.total ?? 1) - 1),
      }));
    }

    return true;
  };

  const handleVisitedFilterChange = async (visitedFilter: string) => {
    const nextFilters = {
      ...filters,
      visitedFilter,
      selectedPage: 1,
    };

    setFilters(nextFilters);
    setActivityLogData((prev) => ({ ...prev, loading: true }));

    const response: any = await dispatch(
      fetchActivityLogData(getActivityLogParams(nextFilters))
    );

    if (response.success) {
      setActivityLogData({ ...response, loading: false });
    } else {
      setActivityLogData((prev) => ({ ...prev, loading: false }));
    }

    document.getElementById("activity-log-table-wrapper")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <Stack mt={1} gap={2}>
      <ActivityLogFilters
        onFiltersChange={handleFiltersChange}
        visitedFilter={filters.visitedFilter}
        onVisitedFilterChange={handleVisitedFilterChange}
      />
      <Stack id="activity-log-table-wrapper">
        <ActivityLogLisTable
          list={activityLogData}
          selectedPage={filters.selectedPage}
          selectedPageSize={filters.selectedPageSize}
          columns={ACTIVITY_LOG_COLUMNS}
          handlePageFilters={handlePageFilters}
          onVisitedChange={handleVisitedChange}
        />
      </Stack>
    </Stack>
  );
}
